import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { IdentityService, type User } from '@/services/identity-service'
import type { DraftUserDTO, UserDTO } from '@bayerntek/identity-lib'
import { jwtDecode } from 'jwt-decode'
import { useStorage } from '@vueuse/core'

type AccessData = {
  token: string
  iss?: string
  sub?: string
  aud?: string[] | string
  exp?: number
  nbf?: number
  iat?: number
  jti?: string
}
type LoggedInData = {
  user: User
  access: AccessData
}

const defaultUser = {
  id: 1,
  username: 'dev',
  full_name: 'dev',
  nick_name: 'dev',
  email: 'dev@localhost',
  phone: 'no-phone',
  roles: ['super_admin'],
  is_two_factor_enabled: false,
  is_passkey_enabled: false,
  created_at: new Date(),
  modified_at: new Date
} as User

const defaultAccess: AccessData = {
  token: '',
}

export const useIdentityStore = defineStore('identity-store', () => {
  const identity_service = IdentityService.getInstance()

  const is_busy = ref(false)

  const users = ref<UserDTO[]>([])
  const current_user = ref<UserDTO | undefined>()

  function set_current_user(value: UserDTO) {
    current_user.value = value
  }
  // JWT
  const auth = useStorage<LoggedInData>('auth-store', {
    user: defaultUser,
    access: defaultAccess,
  })

  function clear_logged_in_data() {
    auth.value = undefined
  }
  function set_logged_in_data(data: LoggedInData) {
    const {
      access: { token = '' },
      user,
    } = data
    const decoded = jwtDecode(token)
    if (decoded) {
      const { exp, iat, aud, iss } = decoded
      const access = {
        token,
        iat,
        exp,
        aud,
        iss,
      }
      auth.value = { user, access }
    } else {
      throw new Error('Invalid token format')
    }
  }
  function patch_logged_in_data(data: LoggedInData) {
    auth.value.user = data.user
    auth.value.access = {
      ...auth.value.access,
      ...data.access,
    }
  }

  const is_authenticated = computed(() => {
    if (!auth.value.access?.token) {
      return false
    }
    const decoded: { exp: number } = jwtDecode(auth.value.access.token)
    const current_time = Math.floor(Date.now() / 1000)
    return decoded.exp > current_time
  })

  // User
  async function create_user(data: DraftUserDTO) {
    is_busy.value = true
    try {
      const { record } = await identity_service.post_user(data)
      return record
    } catch (e) {
      throw e
    } finally {
      is_busy.value = false
    }
  }
  async function patch_user(data: UserDTO) {
    is_busy.value = true
    try {
      const record = await identity_service.patch_user(data)
      return record
    } catch (e) {
      throw e
    } finally {
      is_busy.value = false
    }
  }
  async function check_user(data: { email: string; username?: string }) {
    return identity_service.check_user(data)
  }
  async function get_user(id: number) {
    is_busy.value = true
    try {
      const result = await identity_service.get_user(id)
      current_user.value = result.record
      return result.record
    } catch (e) {
      console.error(e)
      throw e
    } finally {
      is_busy.value = false
    }
  }
  async function delete_user(id: number) {
    is_busy.value = true
    try {
      await identity_service.delete_user(id)
      let index = -1
      for (let i = 0; i < users.value.length; i++) {
        if (users.value[i].id === id) {
          index = i
          break
        }
      }
      users.value.splice(index, 1)
    } catch (e) {
      console.error(e)
      throw e
    } finally {
      is_busy.value = false
    }
  }
  async function get_users(
    offset: number,
    limit: number,
    filter: Record<string, string> = {},
  ) {
    is_busy.value = true
    try {
      const list = await identity_service.get_users(offset, limit, filter)
      users.value = list.records
      return list
    } catch (e) {
      console.error(e)
      throw e
    } finally {
      is_busy.value = false
    }
  }

  // Two factor
  async function two_factor_setup(user_id: number) {
    return identity_service.two_factor_setup(user_id)
  }
  async function two_factor_verify(user_id: number, token: string) {
    return identity_service.two_factor_verify(user_id, token)
  }

  return {
    is_busy,
    // JWT
    auth,
    set_logged_in_data,
    patch_logged_in_data,
    clear_logged_in_data,
    is_authenticated,

    // Users
    users,
    current_user,
    check_user,
    get_user,
    get_users,
    create_user,
    delete_user,
    patch_user,
    set_current_user,
    // Two factor
    two_factor_setup,
    two_factor_verify,
  }
})
